import React, { useState } from 'react';
import Sidebar from '../../components/sidebar/sidebar';
import Header from '../../components/header';
import Card from '../../components/Card';
import ProgressBar from '../../components/ProgressBar/progressbar';
import Styles from './patchcenterStyle.module.scss';
import { useAuth } from 'oidc-react';

import { useNavigate } from 'react-router-dom';
import ApproveForm from './ApproveForm';
import ApproverModal from './ApproveModel';
import Project from '../../common/Utils/project';
import { usePatchForm } from '../../providers/PatchFormProvider';
import { CheckCircle } from 'lucide-react';
import api from '../../components/services/apiService';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useRemediationPlanService } from '../../components/services/useRemediationPlanService';

const Approve = () => {
  const { patchFormData, setPatchFormData, resetPatchFormData } = usePatchForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const auth = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { updateRemediationPlanStatus } = useRemediationPlanService();
  const { selectedTenantId } = useTenantSelection();

  const [isSuccess, setIsSuccess] = useState(false);

  const ApproveFunction = () => {
    setIsModalOpen(true);
  };
  const onClose = () => {
    setIsModalOpen(false);
  };

  // const handleSubmit = async () => {
  //   try {

  //     if (auth && auth.userData) {
  //       delete patchFormData.selectedDevices;
  //       patchFormData.softwareList && delete patchFormData.softwareList;
  //       patchFormData.RemediationPlanPatchList = patchFormData.RemediationPlanPatchList.map(
  //         ({ PackageIdentifier, TargetVersion, BlobURL }) => ({
  //           PackageIdentifier,
  //           TargetVersion,
  //           BlobURL,
  //         }),
  //       );
  //       const token = auth.userData.access_token;
  //       {
  //         const response = await axios.post(
  //           `${Project.api}patchcenterservice/remediationplan/add-remediation-plan`,
  //           JSON.stringify(patchFormData),
  //           {
  //             headers: {
  //               Authorization: `Bearer ${token}`,
  //               'Content-Type': 'application/json',
  //             },
  //           },
  //         );

  //         if (response && response.data) {
  //           setIsModalOpen(false);
  //           setIsSuccess(true);
  //           setTimeout(() => {
  //             navigate('/patchCenter');
  //           }, 2000);
  //         }
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Error updating data:', error);
  //   }
  // };
  const handleSubmit = async (approveAfterSubmit = false) => {
    try {
      setIsSubmitting(true);

      // 🛑 Check for empty actions and patches
      const hasActions = (patchFormData.RemediationPlanActionList || []).length > 0;
      const hasPatches =
        (patchFormData.RemediationPlanPatchList || []).length > 0 ||
        (patchFormData.WindowsOSUpdateList || []).length > 0 ||
        (patchFormData.MacOSUpdateList || []).length > 0 ||
        (patchFormData.LinuxOSUpdateList || []).length > 0;
      if (!hasActions && !hasPatches) {
        addToast('Please select at least one action or patch before submitting.', 'warning', 4000);
        setIsSubmitting(false);
        return;
      }

      if (auth && auth.userData) {
        // 🧩 Step 1: Third-party patches are already prepared in their section
        const remediationPlanPatchList = (patchFormData.RemediationPlanPatchList || []).map((patch) => ({
          PackageIdentifier: patch.PackageIdentifier || patch.packageIdentifier || '',
          TargetVersion: patch.TargetVersion || patch.targetVersion || patch.VersionToBeInstalled || '',
          IsUpdateonly: true,
          IsInstallonly: false,
        }));

        // 🧩 Step 3: Build RemediationPlanAgentList
        const remediationPlanAgentList = patchFormData.deviceSelection.selectedDeviceIds.map((agentId) => ({
          TenantAgentID: agentId,
        }));

        // 🧩 Step 4: Build TargetDevice
        const targetDevice = {
          TargetDeviceFilters: patchFormData?.TargetDevice?.TargetDeviceFilters || {},
          RemediationPlanAgentList: remediationPlanAgentList,
        };

        // 🧩 Step 5: Build RemediationPlanSandboxDevice
        const remediationPlanSandboxDevice = {
          TargetDeviceFilters: patchFormData?.TargetDevice?.TargetDeviceFilters || {},
          RemediationPlanAgentList: patchFormData?.RemediationPlanSandboxDevice || [],
        };

        // 🧩 Step 5: Map RemediationPlanActionList
        const remediationPlanActionList = (patchFormData.RemediationPlanActionList || []).map((action) => ({
          ActionType: action.ActionType,
          WhenToExecute: action.WhenToExecute,
          TenantScriptStoreId: action.TenantScriptStoreId,
          ScriptContent: action.ScriptContent,
          ScriptName: action.ScriptName,
          ScriptDescription: action.ScriptDescription,
          PackageIdentifier: action.PackageIdentifier,
          VersionToBeInstalled: action.VersionToBeInstalled,
          BlobURL: action.BlobURL,
          VersionToBeUninstalled: action.VersionToBeUninstalled,
          RegistryKeyPath: action.RegistryKeyPath,
          RegistryKeyName: action.RegistryKeyName,
          RegistryKeyValue: action.RegistryKeyValue,
          OperationType: action.OperationType,
        }));

        // 🧩 Step 6: Build final payload
        const payload = {
          TenantID: patchFormData.TenantID,
          RemediationPlanData: {
            PlanName: patchFormData.RemediationPlanData?.PlanName,
            IsEmergencyRemediationPlan: patchFormData.RemediationPlanData?.IsEmergencyRemediationPlan,
            SendEmailOnCompletion: patchFormData.RemediationPlanData?.SendEmailOnCompletion,
            RemediationPlanStartTime: patchFormData.RemediationPlanData?.RemediationPlanStartTime,
            IsActive: patchFormData.RemediationPlanData?.IsActive,
          },
          TargetDevice: targetDevice,
          RemediationPlanUserList: patchFormData.RemediationPlanUserList || [],
          RemediationPlanActionList: remediationPlanActionList,
          RemediationPlanPatchList: remediationPlanPatchList,
          WindowsOSUpdateList: (patchFormData.WindowsOSUpdateList || []).map((item) => ({ ...item, selectUpdatesWithoutReboot: false })),
          MacOSUpdateList: (patchFormData.MacOSUpdateList || []).map((item) => ({ ...item, selectUpdatesWithoutReboot: false })),
          LinuxOSUpdateList: (patchFormData.LinuxOSUpdateList || []).map((item) => ({ ...item, selectUpdatesWithoutReboot: false })),
          RemediationPlanSandboxDevice: remediationPlanSandboxDevice,
          RemediationPlanSandboxProgressStatus: patchFormData?.RemediationPlanSandboxProgressStatus || null,
        };

        // 🧩 Step 7: Submit payload
        const response = await api.post(`${Project.api}patchcenterservice/remediationplan/add-remediation-plan`, JSON.stringify(payload));

        if (response && response.data) {
          const planId = response.data.data; // <-- Plan ID from response
          addToast('Plan created successfully', 'success', 4000);

          // 🧩 Step 8: If "Submit & Approve" pressed, approve the plan immediately
          if (approveAfterSubmit && planId) {
            if (!selectedTenantId) {
              addToast('Tenant is not selected. Please select a tenant and try again.', 'error', 4000);
            } else {
              await updateRemediationPlanStatus(selectedTenantId, planId, 'Approved');
              addToast('Plan approved successfully', 'success', 4000);
            }
          }

          setIsModalOpen(false);
          setIsSuccess(true);
          resetPatchFormData();
          setTimeout(() => {
            navigate('/patchCenter');
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error submitting plan:', error);
      addToast('Failed to submit plan', 'error', 4000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const preview = ['Sandbox', 'Selected Devices', 'Actions', 'Patches Available'];
  return (
    <div className="flex">
      <Sidebar></Sidebar>
      <div
        className="bg-[var(--Background-Color,rgb(7,7,7))]

 flex-1"
      >
        <Header title="Preview & Approve" dropdown={2} />

        <Card className="flex-1">
          <div className={`${Styles.progressBar} items-center justify-between mb-0`} style={{ marginLeft: '60px' }}>
            <ProgressBar stepNum={5} identity={'Device select'} />
          </div>
          {isSuccess && (
            <div className="flex items-center p-4 text-green-600 bg-green-100 rounded">
              <CheckCircle className="w-5 h-5 mr-2" />
              <span>Patch Centre created successfully!</span>
            </div>
          )}
          <div className="flex flex-col space-y-6 mt-6">
            {preview.map((heading, index) => (
              <ApproveForm key={index} heading={heading} />
            ))}
          </div>

          <div className="mt-6 flex justify-end space-x-2.5">
            <button
              className="w-42 px-6 py-2 border border-gray-500 rounded-[30px] hover:bg-gray-700"
              //onClick={() => navigate('/patchCenter/Preview')}
              onClick={() => navigate('/patchCenter/AvailablePatch')}
            >
              Previous
            </button>
            {/* Normal Submit */}
            <button className="w-42 px-6 py-2 bg-green-600 rounded-[30px] hover:bg-gray-700" onClick={() => handleSubmit(false)}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>

            {/* Submit & Approve */}
            <button className="w-42 px-6 py-2 bg-red-600 rounded-[30px] shadow-md hover:bg-red-700" onClick={() => handleSubmit(true)}>
              {isSubmitting ? 'Submitting...' : 'Submit&Approve'}
            </button>
          </div>
          {isModalOpen && <ApproverModal onClose={onClose} onDone={handleSubmit} />}
        </Card>
      </div>
    </div>
  );
};

export default Approve;
